/**
 * Главный компонент динамической карточки объекта.
 *
 * Загружает карточку по objectId, рендерит секции и поля по шаблону,
 * поддерживает режимы view / edit.
 */

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, Save, X } from "lucide-react";
import { useObjectCard, useUpdateObjectCard } from "@/hooks/useObjectCard";
import { CardSectionGroup } from "./CardSectionGroup";
import { GeometryTabs } from "./GeometryTabs";
import type {
  CardTemplateField,
  CardTemplateWithDetails,
  CardValuePayload,
  ObjectCardFieldValue,
  ObjectCardGeometryItem,
  ObjectCardResponse,
  ObjectCardUpdateRequest,
  CardValidationErrorItem,
} from "@/types/card-template";

/**
 * Локальный черновик редактирования может не содержать все field_key (вкладка, порядок загрузки,
 * только что изменённые поля). Дополняем PUT снимком с сервера; по каждому ключу приоритет у локальной строки, если в ней есть values.
 */
function mergeObjectFieldRows(
  cardRows: ObjectCardFieldValue[],
  localRows: ObjectCardFieldValue[]
): CardValuePayload[] {
  const localMap = new Map(localRows.map((r) => [r.field_key, r] as const));
  const cardMap = new Map(cardRows.map((r) => [r.field_key, r] as const));
  const keys = new Set<string>([...localMap.keys(), ...cardMap.keys()]);
  const out: CardValuePayload[] = [];
  for (const key of keys) {
    const loc = localMap.get(key);
    if (loc && loc.values.length > 0) {
      out.push(...loc.values);
      continue;
    }
    const base = cardMap.get(key);
    if (base && base.values.length > 0) {
      out.push(...base.values);
    }
  }
  return out;
}

/**
 * Значения только для чтения не отправляем на PUT — иначе merge со снимком сервера
 * подмешивает их в payload и бэкенд возвращает READONLY_FIELD (422).
 */
function omitReadonlyFromCardPayload(
  template: CardTemplateWithDetails | null | undefined,
  objectVals: CardValuePayload[],
  geoVals: { geometry_id: number; values: CardValuePayload[] }[]
): ObjectCardUpdateRequest {
  if (!template?.fields?.length) {
    return { object_values: objectVals, geometries: geoVals };
  }
  const readonlyObjectKeys = new Set(
    template.fields
      .filter(
        (f) =>
          f.is_readonly &&
          f.is_active !== false &&
          f.applies_to !== "geometry"
      )
      .map((f) => f.field_key)
  );
  const readonlyGeometryKeys = new Set(
    template.fields
      .filter(
        (f) =>
          f.is_readonly &&
          f.is_active !== false &&
          f.applies_to === "geometry"
      )
      .map((f) => f.field_key)
  );
  return {
    object_values: objectVals.filter((v) => !readonlyObjectKeys.has(v.field_key)),
    geometries: geoVals.map((g) => ({
      ...g,
      values: g.values.filter((v) => !readonlyGeometryKeys.has(v.field_key)),
    })),
  };
}

/** Снимок данных карточки с сервера: при изменении (откат, ответ PUT, refetch) нужно обновить локальный черновик в режиме edit. */
function cardServerSnapshot(card: ObjectCardResponse): string {
  return JSON.stringify({
    ov: card.object_values,
    geo: card.geometries,
    gar_uuid: card.gar_uuid ?? null,
  });
}

export interface DynamicCardRendererHandle {
  save: () => Promise<void>;
  cancel: () => void;
  isSaving: boolean;
  /** Есть локальные несохранённые правки в режиме edit. */
  isDirty: boolean;
  /** Для создания объекта: собрать тело POST /objects/with-card */
  getPayload?: () => ObjectCardUpdateRequest;
  /** Показать ошибки 422 на полях (создание объекта снаружи не идёт через save). */
  showValidationErrors?: (errors: CardValidationErrorItem[]) => void;
}

interface DynamicCardRendererProps {
  objectId: number;
  focusedGeometryId?: number | null;
  /** Черновик с сервера без запроса GET .../card (создание объекта) */
  initialCardOverride?: ObjectCardResponse | null;
  /** Не вызывать PUT карточки; используется с getPayload и внешним POST /objects/with-card */
  creationMode?: boolean;
  mode: "view" | "edit";
  onModeChange?: (mode: "view" | "edit") => void;
  /** Если false — кнопки Сохранить/Отмена не показываются внизу (управление снаружи через ref). */
  showBottomActionBar?: boolean;
  /** Состояние сохранения / dirty для внешней панели кнопок (шапка ObjectPanel). */
  onToolbarStateChange?: (state: { isSaving: boolean; isDirty: boolean }) => void;
}

export const DynamicCardRenderer = forwardRef<
  DynamicCardRendererHandle,
  DynamicCardRendererProps
>(function DynamicCardRenderer(
  {
    objectId,
    focusedGeometryId = null,
    initialCardOverride = null,
    creationMode = false,
    mode,
    onModeChange,
    showBottomActionBar = true,
    onToolbarStateChange,
  },
  ref
) {
  const shouldFetchCard = !initialCardOverride && objectId > 0;
  const { data: fetchedCard, isLoading, error: loadError } = useObjectCard(
    objectId,
    { enabled: shouldFetchCard }
  );
  const card = initialCardOverride ?? fetchedCard;
  const updateMutation = useUpdateObjectCard(objectId);

  const [localObjectValues, setLocalObjectValues] = useState<
    ObjectCardFieldValue[]
  >([]);
  const [localGeometries, setLocalGeometries] = useState<
    ObjectCardGeometryItem[]
  >([]);
  const [validationErrors, setValidationErrors] = useState<
    CardValidationErrorItem[]
  >([]);

  /** Синхронизация черновика создания при смене типа/организации */
  useEffect(() => {
    if (!initialCardOverride) return;
    setLocalObjectValues(initialCardOverride.object_values);
    setLocalGeometries(initialCardOverride.geometries);
    setValidationErrors([]);
  }, [initialCardOverride]);

  const prevModeRef = useRef<"view" | "edit">(mode);
  /** Последний снимок серверной карточки; при смене (откат, сохранение, refetch) подтягиваем локальный черновик в edit. */
  const prevCardServerSnapshotRef = useRef<string | null>(null);

  /**
   * Синхронизация черновика с сервером:
   * - view — всегда снимок API;
   * - вход в edit из view — копия с сервера;
   * - остаёмся в edit — обновляем локальное состояние только если изменились данные с сервера
   *   (иначе после отката из журнала React Query обновляется, а prev.length > 0 оставлял старый черновик).
   */
  useEffect(() => {
    if (!card) return;
    const snapshot = cardServerSnapshot(card);
    const wasView = prevModeRef.current === "view";
    prevModeRef.current = mode;

    if (mode === "view") {
      setLocalObjectValues(card.object_values);
      setLocalGeometries(card.geometries);
      setValidationErrors([]);
      prevCardServerSnapshotRef.current = snapshot;
      return;
    }

    if (wasView && mode === "edit") {
      setLocalObjectValues(card.object_values);
      setLocalGeometries(card.geometries);
      setValidationErrors([]);
      prevCardServerSnapshotRef.current = snapshot;
      return;
    }

    if (
      prevCardServerSnapshotRef.current === null ||
      prevCardServerSnapshotRef.current !== snapshot
    ) {
      setLocalObjectValues(card.object_values);
      setLocalGeometries(card.geometries);
      setValidationErrors([]);
      prevCardServerSnapshotRef.current = snapshot;
    }
  }, [card, mode]);

  // ── Обработка изменений полей объекта ──────────────────────────────

  const handleObjectFieldChange = useCallback(
    (fieldKey: string, payload: Partial<CardValuePayload>) => {
      setLocalObjectValues((prev) => {
        const idx = prev.findIndex((v) => v.field_key === fieldKey);
        const prevRow = idx >= 0 ? prev[idx].values[0] : undefined;
        /** Слияние: DynamicField шлёт только изменённые ключи; иначе теряются числа/даты других полей. */
        const newPayload: CardValuePayload = {
          ...prevRow,
          ...payload,
          field_key: fieldKey,
          qual_id: payload.qual_id ?? prevRow?.qual_id ?? 0,
        };
        const newItem: ObjectCardFieldValue = {
          field_key: fieldKey,
          applies_to: "object",
          values: [newPayload],
          display_value: null,
        };
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = newItem;
          return copy;
        }
        return [...prev, newItem];
      });
    },
    []
  );

  // ── Обработка изменений полей геометрий ────────────────────────────

  const handleGeometryFieldChange = useCallback(
    (geometryId: number, fieldKey: string, payload: Partial<CardValuePayload>) => {
      setLocalGeometries((prev) =>
        prev.map((geo) => {
          if (geo.id !== geometryId) return geo;
          const idx = geo.values.findIndex((v) => v.field_key === fieldKey);
          const prevRow = idx >= 0 ? geo.values[idx].values[0] : undefined;
          const newPayload: CardValuePayload = {
            ...prevRow,
            ...payload,
            field_key: fieldKey,
            qual_id: payload.qual_id ?? prevRow?.qual_id ?? 0,
          };
          const newVal: ObjectCardFieldValue = {
            field_key: fieldKey,
            applies_to: "geometry",
            geometry_id: geometryId,
            values: [newPayload],
            display_value: null,
          };
          const newValues = [...geo.values];
          if (idx >= 0) {
            newValues[idx] = newVal;
          } else {
            newValues.push(newVal);
          }
          return { ...geo, values: newValues };
        })
      );
    },
    []
  );

  // ── Сборка payload ─────────────────────────────────────────────────

  const buildPayload = useCallback((): ObjectCardUpdateRequest => {
    const objectVals = card
      ? mergeObjectFieldRows(card.object_values, localObjectValues)
      : localObjectValues.flatMap((v) => v.values);

    const geoVals = card
      ? card.geometries.map((cg) => {
          const lg = localGeometries.find((g) => g.id === cg.id);
          const merged = mergeObjectFieldRows(cg.values, lg?.values ?? []);
          return { geometry_id: cg.id, values: merged };
        })
      : localGeometries.map((g) => ({
          geometry_id: g.id,
          values: g.values.flatMap((v) => v.values),
        }));

    return omitReadonlyFromCardPayload(card?.template ?? null, objectVals, geoVals);
  }, [card, localObjectValues, localGeometries]);

  // ── Ошибки → Record ───────────────────────────────────────────────

  const objErrors = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of validationErrors) {
      if (!e.geometry_id) m[e.field_key] = e.message;
    }
    return m;
  }, [validationErrors]);

  const geoErrors = useMemo(() => {
    const m: Record<string, Record<string, string>> = {};
    for (const e of validationErrors) {
      if (e.geometry_id) {
        const gid = String(e.geometry_id);
        if (!m[gid]) m[gid] = {};
        m[gid][e.field_key] = e.message;
      }
    }
    return m;
  }, [validationErrors]);

  // ── Действия ──────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (creationMode) return;
    const payload = buildPayload();
    try {
      const updatedCard = await updateMutation.mutateAsync(payload);
      // Синхронизируем локальный черновик с ответом бэкенда.
      // Это важно, т.к. структура карточки (шаблон/секции/поля) может измениться,
      // а эффекты React Query + смена режима могут сработать не в том порядке.
      setLocalObjectValues(updatedCard.object_values);
      setLocalGeometries(updatedCard.geometries);
      setValidationErrors([]);
      onModeChange?.("view");
    } catch (err: unknown) {
      if (err && typeof err === "object" && "validation_errors" in (err as Record<string, unknown>)) {
        setValidationErrors(
          ((err as Record<string, unknown>).validation_errors as CardValidationErrorItem[]) ?? []
        );
      }
    }
  }, [creationMode, buildPayload, updateMutation, onModeChange]);

  const isDirty = useMemo(() => {
    if (creationMode || mode !== "edit" || !card) return false;
    return (
      JSON.stringify({ ov: localObjectValues, geo: localGeometries }) !==
      JSON.stringify({ ov: card.object_values, geo: card.geometries })
    );
  }, [creationMode, mode, card, localObjectValues, localGeometries]);

  const handleCancel = useCallback(() => {
    if (creationMode && initialCardOverride) {
      setLocalObjectValues(initialCardOverride.object_values);
      setLocalGeometries(initialCardOverride.geometries);
      setValidationErrors([]);
      return;
    }
    if (card) {
      setLocalObjectValues(card.object_values);
      setLocalGeometries(card.geometries);
      setValidationErrors([]);
    }
    onModeChange?.("view");
  }, [creationMode, initialCardOverride, card, onModeChange]);

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const requestCancel = useCallback(() => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    handleCancel();
  }, [isDirty, handleCancel]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      cancel: handleCancel,
      isSaving: creationMode ? false : updateMutation.isPending,
      isDirty,
      getPayload: () => buildPayload(),
      showValidationErrors: (errors) => setValidationErrors(errors),
    }),
    [handleSave, handleCancel, creationMode, updateMutation.isPending, isDirty, buildPayload]
  );

  useEffect(() => {
    onToolbarStateChange?.({
      isSaving: creationMode ? false : updateMutation.isPending,
      isDirty,
    });
  }, [creationMode, updateMutation.isPending, isDirty, onToolbarStateChange]);

  const template = card?.template ?? null;
  const isEditing = mode === "edit";

  const { objectSections, sectionMap } = useMemo(() => {
    if (!template) return { objectSections: [], sectionMap: new Map() };

    const fields = template.fields.filter(
      (f) =>
        f.is_active &&
        f.applies_to !== "geometry"
    );

    const map = new Map<number | null, typeof fields>();
    for (const f of fields) {
      const key = f.section_id ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(f);
    }

    const validSectionIds = new Set(template.sections.map((s) => s.id));
    const orphanLists: CardTemplateField[] = [];
    for (const k of [...map.keys()]) {
      if (k == null) continue;
      if (!validSectionIds.has(k) && (map.get(k)?.length ?? 0) > 0) {
        orphanLists.push(...(map.get(k) ?? []));
        map.delete(k);
      }
    }
    if (orphanLists.length > 0) {
      const atNull = [...(map.get(null) ?? []), ...orphanLists];
      map.set(null, atNull);
    }

    const sectionIdsWithFields = new Set<number>();
    for (const k of map.keys()) {
      if (k != null && (map.get(k)?.length ?? 0) > 0) {
        sectionIdsWithFields.add(k);
      }
    }

    const sections = template.sections
      .filter((s) => sectionIdsWithFields.has(s.id))
      .sort((a, b) => (a.display_order ?? 100) - (b.display_order ?? 100));

    return { objectSections: sections, sectionMap: map };
  }, [template]);

  const hasTemplateObjectFields =
    template != null &&
    (objectSections.some((s) => (sectionMap.get(s.id) ?? []).length > 0) ||
      (sectionMap.get(null) ?? []).length > 0);

  // ── Рендер ────────────────────────────────────────────────────────

  if (shouldFetchCard && isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  }

  if ((shouldFetchCard && loadError) || !card) {
    return (
      <div className="flex items-center gap-2 p-4 text-destructive text-sm">
        <AlertCircle className="h-4 w-4" />
        Не удалось загрузить карточку
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Отображает какой шаблон работает на этой карточке */}
      {template && (
        <p className="text-xs text-muted-foreground">
          Активный шаблон: {template.name}{" "}
          <span className="font-mono">({template.code})</span>
        </p>
      )}
      {/* Секции уровня объекта по шаблону */}
      {objectSections.map((section) => {
          const fields = sectionMap.get(section.id) ?? [];
          if (fields.length === 0) return null;
          return (
            <CardSectionGroup
              key={section.id}
              section={section}
              fields={fields}
              values={localObjectValues}
              errors={objErrors}
              onChange={handleObjectFieldChange}
              readonly={!isEditing}
              objectId={objectId}
              objectGarUuid={card.gar_uuid ?? null}
            />
          );
        })}

      {(sectionMap.get(null) ?? []).length > 0 && (
        <CardSectionGroup
          section={null}
          fields={sectionMap.get(null)!}
          values={localObjectValues}
          errors={objErrors}
          onChange={handleObjectFieldChange}
          readonly={!isEditing}
          objectId={objectId}
          objectGarUuid={card.gar_uuid ?? null}
        />
      )}

      {!hasTemplateObjectFields &&
        template != null &&
        localObjectValues.length > 0 && (
          <p className="text-sm text-muted-foreground py-2">
            Для этого типа объекта нет привязанных полей в опубликованном шаблоне.
            Добавьте поля в админке шаблонов карточек.
          </p>
        )}

      {!hasTemplateObjectFields &&
        localObjectValues.length === 0 &&
        localGeometries.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">
            Нет данных для отображения: для объекта не найден опубликованный шаблон
            карточки или в шаблоне не настроены поля.
          </p>
        )}

      {/* Геометрии */}
      {localGeometries.length > 0 && (
        <>
          <Separator />
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-foreground">
              Атрибуты геометрий по шаблону
            </h4>
            <p className="text-xs text-muted-foreground">
              Поля карточки и KLS для каждой геометрии. Контур и примитив редактируются на вкладке «Геометрия».
            </p>
          </div>
          <GeometryTabs
            geometries={localGeometries}
            template={template ?? null}
            errors={geoErrors}
            onChange={handleGeometryFieldChange}
            readonly={!isEditing}
            activeGeometryId={focusedGeometryId}
            objectId={objectId}
          />
        </>
      )}

      {/* Ошибки валидации (сводка) */}
      {validationErrors.length > 0 && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <p className="font-medium mb-1">
            Ошибки валидации ({validationErrors.length})
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            {validationErrors.slice(0, 5).map((e, i) => (
              <li key={i}>{e.message}</li>
            ))}
            {validationErrors.length > 5 && (
              <li>и ещё {validationErrors.length - 5}...</li>
            )}
          </ul>
        </div>
      )}

      {/* Панель действий внизу (опционально; в embedded-панели кнопки вынесены в шапку) */}
      {showBottomActionBar && isEditing && (
        <div className="flex gap-2 pt-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            <Save className="h-4 w-4 mr-1" />
            {updateMutation.isPending ? "Сохранение..." : "Сохранить"}
          </Button>
          <Button size="sm" variant="ghost" onClick={requestCancel}>
            <X className="h-4 w-4 mr-1" />
            Отмена
          </Button>
        </div>
      )}

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить изменения карточки?</AlertDialogTitle>
            <AlertDialogDescription>
              Несохранённые правки полей будут потеряны.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Вернуться</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowDiscardDialog(false);
                handleCancel();
              }}
            >
              Сбросить без сохранения
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});

DynamicCardRenderer.displayName = "DynamicCardRenderer";
